import {
  IAircraft,
  IAircraftRowSetting,
  ICrewConnection,
  IFlight,
  IFlightFlag,
  IFullFlight,
  IGroundEvent,
  IHighlightFlightSettings,
  IImportFlight,
  IScrollClickResponse,
  sortAircraftEvents,
  unassignedAircraftId,
} from "@vmosolutions/oc-interfaces";
import { IFlightErrorMap } from "src/redux/reducers/FlightScannerReducer";
import { IAircraftFlights, isFlight } from "src/redux/reducers/FlightTimelineReducer";
import { findClosestFlightTimelineEventIndex } from "src/SharedGL/TimelineUtils";
import { BaseTimelineGL } from "../../../SharedGL/BaseTimelineGL";
import { Camera } from "../../../SharedGL/Camera";
import { ScrollBarWrapper } from "../../../SharedGL/ScrollBar/ScrollBarWrapper";
import { loadTexture } from "../../../SharedGL/Shader";
import { TimeAxisLines } from "../../../SharedGL/TimelineLabels/TimeAxisLines";
import { hexToIRGBA, IRGBA, setBeginningDay } from "../TimelineUtils";
import { AircraftRowPainter } from "./AircraftRowPainter";
import { EventErrorsGL } from "./EventErrors/EventErrorsGL";
import { FlightGL, ISettings } from "./Flight/FlightGL";
import { FlightShader } from "./Flight/FlightShader";
import { GroundEventGL } from "./GroundEvent/GroundEventGL";
import { GroundEventShader } from "./GroundEvent/GroundEventShader";
import { HighlightedFlightGL, IHighlighColors } from "./HighlightedFlight/HighlightedFlightGL";
import { SelectedDayGL } from "./SelectedDay/SelectedDayGL";
import { StippledQuadGL } from "./StippledQuad/StippledQuadGL";
import { TimelineLabelsGL } from "./TimelineLabelsGL";
import { TimelineThemeGL } from "../../../SharedGL/TimelineThemeGL";

export const defaultAircraftRowSetting: IAircraftRowSetting = {
  rowHeightPercentage: 1,
  aircraftRowHeight: 36 + 6,
  aircraftRowPadding: 1,
  actualDepartureStart: 6 + 6,
  actualDepartureHeight: 20,
  scheduledStart: 2,
  scheduledHeight: 4 + 6,
  aircraftRowTotalHeight: 36 + 6 + 1,
};
interface IRenderers {
  lastProcessedTime: number;
  flights: FlightGL;
  groundEvents: GroundEventGL;
  eventErrors: EventErrorsGL;
}
interface IAircraftFlightBuffer {
  [id: string]: IRenderers;
}

interface IAircraftEventsInRange {
  [id: string]: {
    events: (IFlight | IGroundEvent | IFullFlight)[];
  };
}

export interface ITimelineSelections {
  selectedDay: Date;
  selectedAircraft: string;
}

interface IConnection {
  xStart: number;
  index1: number;
  xEnd: number;
  index2: number;
  color: string;
  index: number; //generally 0
}
/**
 * TimelineGL the base renderer for the Event timeline.
 * Renders events/selected events, highlights, event labels
 */
export class TimelineGL {
  private mount: HTMLElement;
  private theme: TimelineThemeGL;
  private canvas: HTMLCanvasElement;
  public loseContextExt: WEBGL_lose_context | null;
  private gl: WebGLRenderingContext;

  private timeAxisLines: TimeAxisLines;
  private aircraftBuffers: IAircraftFlightBuffer = {};
  private connections: IConnection[];
  private hoveredEvent: IFlight | IGroundEvent;
  private selectedEvents: (IFlight | IGroundEvent)[] = [];
  private reassignedEvents: (IFlight | IGroundEvent)[] = [];
  private focusedEvents: (IFlight | IGroundEvent | IImportFlight)[] = [];
  private sortedAircraftEventsInRange: IAircraftEventsInRange = {};

  private hoveredFlightGL: HighlightedFlightGL;
  private timelineLabelsGL: TimelineLabelsGL;
  private aircraftRowPainter: AircraftRowPainter;
  private selectedDayGL: SelectedDayGL;
  private selectedAircraftGL: StippledQuadGL;
  private scrollBarWrapper: ScrollBarWrapper;
  private maxHeight: number;
  private hoveredTime: number = null;
  public timelineStart: number = BaseTimelineGL.timelineStart;

  private camera: Camera;
  private maxRows = 0;

  private flightShader: FlightShader;
  private groundEventShader: GroundEventShader;
  private groundEventTexture: WebGLTexture;

  private scrollBarThumbColor: IRGBA;
  private selectedEventOutlineColor: IRGBA;
  private hoveredEventOutlineColor: IRGBA;
  private focusedEventOutlineColor: IRGBA;

  private timelineSettings: ISettings;
  private timelineHighlightLastApplied: number | null; // Epoch value stores last applied timestamp for highlight.
  private currentDateOffsetHours: number;
  private viewScale: string;
  constructor(
    mount: HTMLElement,
    width: number,
    height: number,
    font = "Arial",
    baseFontSize = 10,
    flightFlags: IFlightFlag[],
    timelineSettings: ISettings
  ) {
    this.mount = mount;
    this.canvas = document.createElement("canvas");
    const devicePixelRatio = window.devicePixelRatio || 1;
    this.canvas.width = width * devicePixelRatio;
    this.canvas.height = height * devicePixelRatio;

    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.gl = this.canvas.getContext("webgl");
    this.loseContextExt = this.gl.getExtension("WEBGL_lose_context");
    if (!this.loseContextExt) {
      console.error("WEBGL_lose_context is not supported");
    }
    this.gl.enable(this.gl.DEPTH_TEST);
    this.gl.enable(this.gl.BLEND);
    this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);
    this.mount.appendChild(this.canvas);

    this.camera = new Camera([-width / 2.0, width / 2.0], [0, height]);

    this.timelineLabelsGL = new TimelineLabelsGL(mount, width, height, font, baseFontSize, flightFlags, null, {
      overdueDepartureMovementMsgMinutes: timelineSettings.overdueDepartureMovementMsgMinutes,
      overdueArrivalMovementMsgMinutes: timelineSettings.overdueArrivalMovementMsgMinutes,
    });

    const white = { red: 1.0, green: 1.0, blue: 1.0, alpha: 1.0 };

    this.aircraftRowPainter = new AircraftRowPainter(this.gl);
    this.timeAxisLines = new TimeAxisLines(this.gl);
    this.selectedDayGL = new SelectedDayGL(this.gl);
    this.connections = [];
    this.hoveredFlightGL = new HighlightedFlightGL(this.gl, white);

    this.theme = new TimelineThemeGL();
    this.selectedAircraftGL = new StippledQuadGL(this.gl);
    this.scrollBarWrapper = new ScrollBarWrapper(this.gl);
    this.scrollBarThumbColor = this.theme.scrollBarThumb;

    this.flightShader = new FlightShader(this.gl);
    this.groundEventShader = new GroundEventShader(this.gl);
    this.groundEventTexture = loadTexture(this.gl, "/assets/timeline-icons/diagonal-pattern-256.png");
    this.selectedEventOutlineColor = this.theme.selectedFlightColor;
    this.hoveredEventOutlineColor = this.theme.hoverFlightColor;
    this.focusedEventOutlineColor = this.theme.hoverFlightColor;

    this.timelineSettings = timelineSettings;
    this.setIsUTC(timelineSettings.utc);
    this.setIsICAO(timelineSettings.icao);
    this.timelineHighlightLastApplied = new Date()?.valueOf();
    this.currentDateOffsetHours = timelineSettings?.currentDateOffsetHours ?? 0;
    this.viewScale = timelineSettings.viewScale;
  }

  /**
   * updateFlights updates the graphics buffers when the event data changes. Only updates new rows
   * @param data - the aircraft events to update the buffers
   * @param aircrafts - the list of aircrafts to iterate each row
   */
  public updateFlights = (
    data: IAircraftFlights,
    aircrafts: IAircraft[],
    aircraftErrors: IFlightErrorMap,
    highlightFlightSettings?: IHighlightFlightSettings
  ): void => {
    // Update FlightGL buffers

    // If null, assume that the timeline has just been mounted and set a value as otherwise it will not load default saved values.
    const savedAppSettingsHaveHighlight = !highlightFlightSettings?.lastApplied && highlightFlightSettings?.active;
    const lastHighlightAppliedTime = savedAppSettingsHaveHighlight
      ? new Date()?.valueOf()
      : (highlightFlightSettings?.lastApplied ?? this.timelineHighlightLastApplied);
    //!! Uncomment console log before for testing purposes, should show true,followed by false if working correctly.
    //console.log(
    //  lastHighlightAppliedTime > this.timelineHighlightLastApplied && !savedAppSettingsHaveHighlight,
    //  savedAppSettingsHaveHighlight
    //);

    // Debug.log(data)
    aircrafts.forEach((aircraft: IAircraft, index: number) => {
      const aircraftId = aircraft._id ?? unassignedAircraftId;
      if (!data[aircraftId]) {
        return;
      }

      if (!this.aircraftBuffers[aircraftId]) {
        this.aircraftBuffers[aircraftId] = {
          flights: new FlightGL(this.gl, this.flightShader, this.timelineSettings),
          groundEvents: new GroundEventGL(this.gl, this.groundEventShader, this.groundEventTexture),
          lastProcessedTime: new Date(0).getTime(),
          eventErrors: new EventErrorsGL(this.gl, this.groundEventShader, this.groundEventTexture),
        };
      }
      const lastProcessedTime = data[aircraftId].lastProcessedTime;
      // Update Flight buffers only if lastProcessedTime or lastApplied highlight is more recent than what's stored in buffer.
      if (
        this.aircraftBuffers[aircraftId].lastProcessedTime < lastProcessedTime ||
        (lastHighlightAppliedTime > this.timelineHighlightLastApplied && !savedAppSettingsHaveHighlight) ||
        savedAppSettingsHaveHighlight
      ) {
        const rowFlights: IFlight[] = data[aircraftId].flights;
        const groundEvents: IGroundEvent[] = data[aircraftId].groundEvents;
        this.aircraftBuffers[aircraftId].flights.updateBuffers(this.gl, rowFlights, highlightFlightSettings);
        this.aircraftBuffers[aircraftId].groundEvents.updateBuffers(this.gl, groundEvents, highlightFlightSettings);
        this.aircraftBuffers[aircraftId].lastProcessedTime = lastProcessedTime;
      }
    });

    // Set this outside of the aircraft loop as it doesn't need to be stored individually for each aircraft.
    this.timelineHighlightLastApplied = lastHighlightAppliedTime;
  };

  /**
   * updateFlightFlags updates the flags for a fligth
   * @param flightFlags - the flightFlags to update
   */
  public updateFlightFlags = (flightFlags: IFlightFlag[]): void => {
    this.timelineLabelsGL.updateFlightFlags(flightFlags);
  };

  public updateErrors = (
    flights: IAircraftFlights,
    flightErrorsMap: IFlightErrorMap,
    highlightFlightSettings: IHighlightFlightSettings
  ): void => {
    const aircrafts = Object.keys(this.aircraftBuffers);
    for (const aircraftId of aircrafts) {
      const aircraftFlights = flights[aircraftId]?.flights;
      const flightErrors = flightErrorsMap[aircraftId]?.errors;
      if (!flightErrors || !aircraftFlights) {
        continue;
      }
      this.aircraftBuffers[aircraftId].eventErrors.updateBuffers(
        this.gl,
        flightErrors,
        aircraftFlights,
        highlightFlightSettings
      );
    }
  };

  /** getTheme @returns the theme for TimelineGL */
  public getTheme = (): TimelineThemeGL => this.theme;
  /** setTheme @param theme - theme for TimelineGL */
  public setTheme = (theme: any): void => {
    this.theme.updateTheme(theme);
  };

  public setHoveredTime = (time: number): void => {
    this.hoveredTime = time;
  };

  /**
   * setViewport sets the webGL context's viewport to the specified width/height so rendering only takes place there
   * @param width - the width of the viewport
   * @param height - the height of the viewport
   */
  public setViewport = (width: number, height: number): void => {
    const devicePixelRatio = window.devicePixelRatio || 1;
    const scaledWidth = width * devicePixelRatio;
    const scaledHeight = height * devicePixelRatio;

    this.canvas.width = scaledWidth;
    this.canvas.height = scaledHeight;

    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    const scaledTimelineStart = BaseTimelineGL.timelineStart * devicePixelRatio;
    const [minY] = this.camera.getYRange();
    const maxRange = this.maxRows * FlightGL.aircraftRowTotalHeight;
    this.maxHeight = maxRange;
    let newMaxY = minY + height - BaseTimelineGL.timelineStart;
    let newMinY = minY;
    if (newMaxY > maxRange) {
      const delta = newMaxY - maxRange;
      newMaxY = maxRange;
      newMinY = Math.max(0, minY - delta);
    }
    this.camera.setYRange([newMinY, newMaxY]);

    if (maxRange >= height - BaseTimelineGL.timelineStart) {
      this.gl.viewport(0, 0, scaledWidth, scaledHeight - scaledTimelineStart);
      this.timelineLabelsGL.setDimensions(width, height);
    } else {
      const scaledMaxRange = maxRange * devicePixelRatio;
      this.gl.viewport(0, scaledHeight - scaledMaxRange - scaledTimelineStart, scaledWidth, scaledMaxRange);
      this.timelineLabelsGL.setDimensions(width, maxRange + BaseTimelineGL.timelineStart);
    }
    this.recalculateScrollBar();
  };

  /**
   * setTimeAxis sets the xRange of the camera
   * @param startTime - the start of the xRange for camera
   * @param endTime - the end of the xRange for camera
   */
  public setTimeAxis = (startTime: number, endTime: number): void => {
    this.camera.setXRange([startTime, endTime]);
  };

  /**
   * getTimeRange
   * @returns the xRange of the camera
   */
  public getTimeRange = (): number[] => this.camera.getXRange();

  /**
   * pixelCoordsToTimeAxis converts from pixels to time using camera
   * @param pixels - the pixel value to convert to time
   */
  public pixelCoordsToTimeAxis = (pixels: number): number => {
    return this.camera.pixelCoordsToTimeAxis(pixels, this.canvas.width);
  };

  /**
   * pixelCoordsToEpochTime converts from pixels to time using camera
   * @param pixels - the pixel value to convert to epoch time
   */
  public pixelCoordsToEpochTime = (pixels: number): number => {
    return this.camera.pixelCoordsToEpochTime(pixels, this.canvas.width);
  };

  /**
   * timeAxisToPixelCoords converts a time value to pixel coords
   * @param time - the time to convert to a pixel value
   */
  public timeAxisToPixelCoords = (time: number): number => {
    // return this.camera.timeAxisToPixelCoords(time, this.canvas.width);
    // wasn't sure how this would effect the other uses of timeAxisToPixelCoords
    return this.camera.timeAxisToPixelCoords(time, this.canvas.width / window.devicePixelRatio);
  };

  /**
   * getAircraftFromYPixel converts a yPixel to the aircraft associated with it
   * @param yPixels - the y pixel to convert to aircraft index
   * @returns the index of the aircraft the yPixel is associated with
   */
  public getAircraftFromYPixel = (yPixels: number): number => {
    const [yMin] = this.camera.getYRange();
    const y = yMin + (yPixels - BaseTimelineGL.timelineStart);
    return Math.floor(y / FlightGL.aircraftRowTotalHeight);
  };

  /**
   * getAircraftIfOnEventTarget converts a  yPixel to the index of the aircraft only
   * if the yPixel is on the target area of the event
   * @param yPixels - the pixel to check if is on target
   * @returns the index of the aircraft that the flight is on or -1 if the pixel is not on target
   */
  public getAircraftIfOnEventTarget = (yPixels: number): { aircraftIndex: number; isScheduled: boolean } => {
    const [yMin] = this.camera.getYRange();
    const y = yMin + (yPixels - BaseTimelineGL.timelineStart);
    //check if you are on a flight...
    const index = Math.floor(y / FlightGL.aircraftRowTotalHeight);
    const positionInRow = y - index * FlightGL.aircraftRowTotalHeight;
    const targetStart = FlightGL.scheduledStart;
    const targetEnd = FlightGL.actualDepartureStart + FlightGL.actualDepartureHeight;
    return {
      aircraftIndex: positionInRow >= targetStart && positionInRow <= targetEnd ? index : -1,
      isScheduled: positionInRow >= targetStart && positionInRow <= FlightGL.actualDepartureStart,
    };
  };

  public getYPixelFromAircraft = (aircraftId: string, aircrafts: IAircraft[]): number => {
    const index = aircrafts.findIndex((aircraft: IAircraft) => aircraft._id === aircraftId);
    if (index < 0) {
      return -100;
    }
    const [yMin] = this.camera.getYRange();
    return BaseTimelineGL.timelineStart + FlightGL.aircraftRowTotalHeight * index - yMin;
  };
  /**
   * moveCameraByXPixels pans the camera x pixels
   * @param pixels - num pixels to pan camera
   */
  public moveCameraByXPixels = (pixels: number): void => {
    const [minTime, maxTime] = this.camera.getXRange();
    const time = this.pixelCoordsToTimeAxis(pixels);
    this.setTimeAxis(minTime + time, maxTime + time);
  };

  /**
   * moveCameraToTime sets the camera to be centered on xTime
   * @param xTime - the time to center the camera on
   */
  public moveCameraToTime = (xTime: number): void => {
    const [minTime, maxTime] = this.camera.getXRange();
    const halfWidth = (maxTime - minTime) / 2.0;
    this.setTimeAxis(xTime - halfWidth, xTime + halfWidth);
  };

  /**
   * setMaxRows the maximum number of aircraft rows. Helpful for knowing if you are at end of vertical range
   * @param max - the max number of rows
   */
  public setMaxRows = (max: number): void => {
    this.maxRows = max;
  };

  /**
   * moveCameraByRow pans the camera by rows amount of rows
   * @param rows - the number of rows to move the camera by
   */
  public moveCameraByRow = (rows: number): void => {
    this.moveCameraByYPixels(rows * FlightGL.aircraftRowTotalHeight);
  };

  /**
   * moveCameraByYPixels  pans the camera vertically by pixels amount
   * @param pixels - the amount to pan the camera vertically
   */
  public moveCameraByYPixels = (pixels: number): void => {
    const [minY, maxY] = this.camera.getYRange();
    const delta = maxY - minY;

    let [newMinY, newMaxY] = [minY + pixels, maxY + pixels];
    const totalMax = this.maxRows * FlightGL.aircraftRowTotalHeight;
    if (minY + pixels < 0) {
      newMinY = 0;
      newMaxY = newMinY + delta;
    } else if (maxY + pixels > totalMax) {
      newMaxY = totalMax;
      newMinY = newMaxY - delta;
    }
    this.camera.setYRange([newMinY, newMaxY]);
  };

  /**
   * zoomCamera zooms the camera by deltaZoom
   * @param deltaZoom - the amount to zoom the camera in or out
   */
  public zoomCamera = (deltaZoom: number): void => {
    const zoomTime = this.pixelCoordsToTimeAxis(deltaZoom);
    this.camera.zoom(zoomTime);
  };

  /**
   * setHoveredEvent sets hovered event so that it can be rendered
   * @param event - the event to hover
   */
  public setHoveredEvent = (event: IFlight | IGroundEvent): void => {
    this.hoveredEvent = event;
  };

  /**
   * getHoveredEvent returns the current hoveredEvent one event can be hovered at a time
   * @returns the current hovered event
   */
  public getHoveredEvent = (): IFlight | IGroundEvent => {
    return this.hoveredEvent;
  };

  /**
   * getYRange gets the yRange of the camera
   * @returns number[] containing the yRange of the camera
   */
  public getYRange = (): number[] => {
    return this.camera.getYRange();
  };

  /**
   * jumpToFlight moves camera to the centered location of the event
   * @param event - the event to jump to
   * @param aircrafts - the list of aircrafts so we know vertically where to move camera
   */
  public jumpToFlight = (event: IFlight | IGroundEvent | IImportFlight, aircafts: IAircraft[]): number => {
    const [xMin, xMax] = this.camera.getXRange();
    const width = xMax - xMin;
    const eventXMin = isFlight(event) ? new Date(event.actualOut).getTime() : new Date(event.actualStart).getTime(); //Block Time switch to Out/In
    const eventXMax = isFlight(event) ? new Date(event.actualIn).getTime() : new Date(event.actualEnd).getTime(); //Block Time switch to Out/In
    const eventWidth = eventXMax - eventXMin;

    const timeToJump = eventXMin + eventWidth / 2.0;
    const halfWidth = width / 2.0;
    this.camera.setXRange([timeToJump - halfWidth, timeToJump + halfWidth]);

    const aircraftIndex = this.getAircraftIndexFromEvent(event, aircafts);
    const totalHeight = this.maxRows * FlightGL.aircraftRowTotalHeight;
    if (aircraftIndex >= 0) {
      const [yMin, yMax] = this.camera.getYRange();
      const halfHeight = (yMax - yMin) / 2.0;
      const yPos = FlightGL.aircraftRowTotalHeight * aircraftIndex; // - BaseTimelineGL.timelineStart;
      let currentYMin = yPos - halfHeight;
      let currentYMax = yPos + halfHeight;
      if (currentYMin < 0 && currentYMax > totalHeight) {
        return -1;
      } else if (currentYMin < 0) {
        currentYMin = 0;
        currentYMax = halfHeight * 2.0;
      } else if (currentYMax > totalHeight) {
        currentYMin = totalHeight - halfHeight * 2.0;
        currentYMax = totalHeight;
      }
      this.camera.setYRange([currentYMin, currentYMax]);
      return currentYMin;
    }
    return -1;
  };

  //set the focused events outline color
  public setFocusedEventsOutlineColor = (defaultColor: boolean): void => {
    this.focusedEventOutlineColor = defaultColor ? this.theme.selectedFlightColor : this.theme.hoverFlightColor;
  };

  /**
   * getFocusedEventsOutlineColor returns true if the deafult color is the current color
   */
  public getFocusedEventsOutlineColor = (): boolean => {
    return this.focusedEventOutlineColor === this.theme.hoverFlightColor;
  };

  /**
   * appendSelectedFlights appends to the selectedEvents array the given events
   * @param selectedEvents - the events to be appended
   */
  public appendSelectedFlights = (selectedEvents: (IFlight | IGroundEvent)[]): void => {
    this.selectedEvents = [...this.selectedEvents, ...selectedEvents];
  };

  /**
   * setSelectedEvents set the events selected by user to be drawn here
   * @param selectedEents - the events selected by user
   */
  public setSelectedEvents = (selectedEvents: (IFlight | IGroundEvent)[]): void => {
    this.selectedEvents = [...selectedEvents];
  };

  public setFocusedEvents = (focusedEvents: (IFlight | IGroundEvent | IImportFlight)[]): void => {
    this.focusedEvents = [...focusedEvents];
  };

  /**
   * setReassignedFlights draws the ghost events of the reassigned events
   * @param reassignedEvents - the events that are now to be reassigned
   */
  public setReassignedFlights = (reassignedEvents: (IFlight | IGroundEvent)[]): void => {
    this.reassignedEvents = [...reassignedEvents];
  };

  /**
   * setSelectedEvents set the events selected by user to be drawn here
   * @param selectedEents - the events selected by user
   */
  public setCrewConnections = (crewConnections: ICrewConnection[], aircrafts: IAircraft[]): void => {
    this.mapConnections([...crewConnections], aircrafts);
  };

  /**
   * Set the timeline display to show the flight label in local time or UTC
   * @param value - true for the times to be UTC false for local
   */
  public setIsUTC = (value: boolean): void => {
    this.timelineLabelsGL.setIsUTC(value);
  };

  /**
   * Set the timeline display to show the flight origin/dest label as IATA or ICAO
   * @param value - true for the times to be ICAO false for IATA
   */
  public setIsICAO = (value: boolean): void => {
    this.timelineLabelsGL.setIsICAO(value);
  };

  public setCurrentDateOffsetHours = (value: number): void => {
    this.currentDateOffsetHours = value;
  };
  public getCurrentDateOffsetHours = (): number => {
    return this.currentDateOffsetHours;
  };

  public setViewScale = (value: string): void => {
    this.viewScale = value;
  };
  public getViewScale = (): string => {
    return this.viewScale;
  };

  /**
   * Get the events on either side of your where you are hovering
   * @param hoveredTime - xTime of hovering
   * @param aircraftId - aircraft Id for the yRow
   * @returns - the events before and after where your mouse is
   */
  public getPreviousNextEvents = (
    hoveredTime: number,
    aircraftId: string
  ): {
    prevEvent: IFlight | IFullFlight | IGroundEvent;
    nextEvent: IFlight | IFullFlight | IGroundEvent;
    prevFlight: IFlight | IFullFlight;
    nextFlight: IFlight | IFullFlight;
    groundEvents: IGroundEvent[];
  } => {
    const eventsOnAircraft = this.sortedAircraftEventsInRange[aircraftId]?.events ?? [];
    //this returns the closest event.. but is the event to the left or right of the event
    const closestEventIndex = findClosestFlightTimelineEventIndex(
      eventsOnAircraft,
      hoveredTime,
      "actualOff",
      "actualStart"
    );
    const closestEvent = eventsOnAircraft[closestEventIndex];
    const closestEventStartTime = closestEvent
      ? isFlight(closestEvent)
        ? closestEvent.actualOff
        : closestEvent.actualStart
      : undefined;

    //is this event before or after the mouse position
    let nextEvent: IFlight | IFullFlight | IGroundEvent = undefined;
    let prevEvent: IFlight | IFullFlight | IGroundEvent = undefined;
    let nextFlight: IFlight | IFullFlight = undefined;
    let prevFlight: IFlight | IFullFlight = undefined;
    let nextFlightIndex: number;
    let prevFlightIndex: number;
    const groundEvents: IGroundEvent[] = [];

    if ((closestEventStartTime as number) > hoveredTime) {
      //then the closestEvent is the nextEvent
      nextEvent = closestEvent;
      prevEvent = eventsOnAircraft[closestEventIndex - 1];
      [nextFlight, nextFlightIndex] = this.getValidFlight(closestEventIndex, eventsOnAircraft, true);
      [prevFlight, prevFlightIndex] = this.getValidFlight(closestEventIndex - 1, eventsOnAircraft, false);
    } else {
      //else the closestEvent is the nextEvent
      nextEvent = eventsOnAircraft[closestEventIndex + 1];
      prevEvent = closestEvent;
      [nextFlight, nextFlightIndex] = this.getValidFlight(closestEventIndex + 1, eventsOnAircraft, true);
      [prevFlight, prevFlightIndex] = this.getValidFlight(closestEventIndex, eventsOnAircraft, false);
    }

    for (let n = prevFlightIndex + 1; n < nextFlightIndex; n++) {
      groundEvents.push(eventsOnAircraft[n] as IGroundEvent);
    }

    return { prevEvent, nextEvent, prevFlight, nextFlight, groundEvents };
  };

  private getValidFlight = (
    index: number,
    eventsOnAircraft: any,
    forward: boolean
  ): [IFlight | IFullFlight, number] => {
    let currentIndex = index;
    if (isFlight(eventsOnAircraft[currentIndex])) {
      return [eventsOnAircraft[currentIndex], currentIndex];
    } else {
      while (currentIndex < eventsOnAircraft.length && currentIndex > 0) {
        if (forward) currentIndex++;
        else currentIndex--;

        if (isFlight(eventsOnAircraft[currentIndex])) return [eventsOnAircraft[currentIndex], currentIndex];
      }
    }
    return [undefined, undefined];
  };

  /**
   * getAircraftIndexFromEvent finds the aircraft index given a specific event
   * @param event - the event to search for aircrafts with
   * @param aircrafts - the list of aircrafts to search
   * @returns the aircraft index or -1 if not found
   */
  public getAircraftIndexFromEvent = (
    event: IFlight | IGroundEvent | IImportFlight,
    aircrafts: IAircraft[]
  ): number => {
    try {
      return aircrafts.findIndex((aircraft: IAircraft) => aircraft._id === event.aircraft._id);
    } catch (e) {
      return -1;
    }
  };

  /**
   * drawHighlights actually draws the highglighted events
   */
  private drawHighlights = (
    highlightedFlights: (IFlight | IGroundEvent | IImportFlight)[],
    aircrafts: IAircraft[],
    colors: IHighlighColors
  ): void => {
    highlightedFlights.forEach((highlightedFlight: IFlight | IGroundEvent | IImportFlight) => {
      const flightIndex = this.getAircraftIndexFromEvent(highlightedFlight, aircrafts);
      const [actualStart, actualEnd] = isFlight(highlightedFlight) //Block Time switch to Out/In
        ? [new Date(highlightedFlight.actualOut).getTime(), new Date(highlightedFlight.actualIn).getTime()]
        : [new Date(highlightedFlight.actualStart).getTime(), new Date(highlightedFlight.actualEnd).getTime()];
      const yStart = FlightGL.aircraftRowTotalHeight * flightIndex;
      const actualYPos = isFlight(highlightedFlight)
        ? [yStart + FlightGL.actualDepartureStart, FlightGL.actualDepartureHeight]
        : [yStart + FlightGL.scheduledStart, FlightGL.actualDepartureStart + FlightGL.actualDepartureHeight];
      this.hoveredFlightGL.drawHighlightedFlight(this.gl, this.camera, [actualStart, actualEnd], actualYPos, colors);

      if (isFlight(highlightedFlight)) {
        const [scheduledDep, scheduledArr] = [
          //Block Time switch to Out/In
          new Date(highlightedFlight.scheduledOut).getTime(),
          new Date(highlightedFlight.scheduledIn).getTime(),
        ];
        const flightDelay = Math.abs(scheduledDep - actualStart);
        if (flightDelay > 0) {
          this.hoveredFlightGL.drawHighlightedFlight(
            this.gl,
            this.camera,
            [scheduledDep, scheduledArr],
            [yStart + FlightGL.scheduledStart, FlightGL.scheduledHeight],
            colors
          );
        }
      }
    });
  };

  //if points are drawn above the timelinestart, get the updated coordinates
  private getXfromY = (x1: number, y1: number, x2: number, y2: number, yStart: number): number => {
    //! don't divide by zero!!
    if (x2 - x1 === 0) {
      return 0;
    }
    const slope = (y2 - y1) / (x2 - x1);
    const b = y1 - slope * x1;

    return (yStart - b) / (slope !== 0 ? slope : 1);
  };

  private getCanvasConnection = (
    flightA: IFlight,
    flightB: IFlight,
    aircrafts: IAircraft[],
    color = "#FFFFFF"
  ): IConnection => {
    const index1 = this.getAircraftIndexFromEvent(flightA, aircrafts);
    const index2 = this.getAircraftIndexFromEvent(flightB, aircrafts);

    return { xStart: flightA.actualIn?.valueOf(), index1, xEnd: flightB.actualOut?.valueOf(), index2, color, index: 0 };
  };

  private connectionLineIndex = (connection: IConnection, existingConnections: IConnection[]): number => {
    const { color, xStart, index1, xEnd, index2 } = connection;
    const alreadyExists = existingConnections.find(
      (c: IConnection) =>
        c.index1 === index1 && c.index2 === index2 && c.xStart === xStart && c.xEnd === xEnd && c.color === color
    );
    if (alreadyExists) {
      return -1;
    }
    const all = existingConnections.filter(
      (c: IConnection) =>
        c.index !== -1 &&
        ((c.index1 === index1 && c.index2 === index2 && Math.max(c.xStart, xStart) <= Math.min(c.xEnd, xEnd)) ||
          (c.xStart === xStart && c.xEnd === xEnd && Math.max(c.index1, index1) <= Math.min(c.index2, index2)))
    );
    return all.length;
  };

  private mapConnections = (crewConnections: ICrewConnection[], aircrafts: IAircraft[]): void => {
    const mappedConnection: IConnection[] = [];
    crewConnections.forEach((connection: ICrewConnection) => {
      const { target, color, prev, next } = connection;

      //prev flight is A, target flight is B
      prev.forEach((p, index) => {
        if (prev[index + 1]) {
          const connection = this.getCanvasConnection(
            p.flight as IFlight,
            prev[index + 1].flight as IFlight,
            aircrafts,
            color
          );
          connection.index = this.connectionLineIndex(connection, mappedConnection);
          mappedConnection.push(connection);
        } else {
          const connection = this.getCanvasConnection(p.flight as IFlight, target.flight as IFlight, aircrafts, color);
          connection.index = this.connectionLineIndex(connection, mappedConnection);
          mappedConnection.push(connection);
        }
      });
      //target flight is A, next flight is B
      next.forEach((n, index) => {
        console.log(index);
        if (index === 0) {
          const connection = this.getCanvasConnection(target.flight as IFlight, n.flight as IFlight, aircrafts, color);
          connection.index = this.connectionLineIndex(connection, mappedConnection);
          mappedConnection.push(connection);
        }
        if (next[index + 1]?.flight) {
          const connection = this.getCanvasConnection(
            n.flight as IFlight,
            next[index + 1].flight as IFlight,
            aircrafts,
            color
          );
          connection.index = this.connectionLineIndex(connection, mappedConnection);
          mappedConnection.push(connection);
        }
      });
    });
    this.connections = mappedConnection;
  };

  private drawConnection = (): void => {
    const [yMin] = this.camera.getYRange();
    this.connections.forEach((c: IConnection) => {
      // const flightA = this.selectedEvents?.[0] as IFlight;
      const { color, index, xStart, index1, xEnd, index2 } = c;
      const y1 =
        (this.timelineStart + FlightGL.aircraftRowTotalHeight * (index1 + 0.5) - yMin) * window.devicePixelRatio;
      const x1 = this.camera.timeAxisToPixelCoords(xStart, this.canvas.width);
      // const flightB = this.selectedEvents?.[1] as IFlight;
      const y2 =
        (this.timelineStart + FlightGL.aircraftRowTotalHeight * (index2 + 0.5) - yMin) * window.devicePixelRatio;
      //indexes aren't changing if you scroll - just if you change the aircraft filters
      const x2 = this.camera.timeAxisToPixelCoords(xEnd, this.canvas.width);
      //don't draw this off screen
      if (y1 < this.timelineStart && y2 < this.timelineStart) {
        return;
      }
      //do something to figure out what the x would be at y === this.timelineStart
      this.timelineLabelsGL.drawFlightConnections(
        y1 < this.timelineStart ? this.getXfromY(x1, y1, x2, y2, this.timelineStart) : x1,
        y1 < this.timelineStart ? this.timelineStart : y1,
        y2 < this.timelineStart ? this.getXfromY(x1, y1, x2, y2, this.timelineStart) : x2,
        y2 < this.timelineStart ? this.timelineStart : y2,
        color,
        index
      );
    });
  };
  /**
   * drawHighlightedFlights draws the events that are selected by the user
   * @param aircrafts - the list of aircrafts
   */
  private drawHighlightedFlights = (aircrafts: IAircraft[]): void => {
    const basicFillColor: IRGBA = { red: 0, green: 0, blue: 0, alpha: 0.25 };
    const ghostFlight: IRGBA = { red: 1, green: 1, blue: 1, alpha: 0.25 };

    this.drawHighlights(this.focusedEvents, aircrafts, {
      outline: this.focusedEventOutlineColor,
      fill: basicFillColor,
    });

    // if (this.hoveredEvent && !this.selectedEvents.includes(this.hoveredEvent)) {
    //   this.drawHighlights([this.hoveredEvent], aircrafts, {
    //     outline: this.theme.hoverFlightColor,
    //     fill: basicFillColor,
    //   });
    // }

    if (this.hoveredEvent) {
      this.drawHighlights([this.hoveredEvent], aircrafts, {
        outline: this.theme.hoverFlightColor,
        fill: basicFillColor,
      });
    }

    this.drawHighlights(this.selectedEvents, aircrafts, {
      outline: this.theme.selectedFlightColor,
      fill: basicFillColor,
    });

    this.drawHighlights(this.reassignedEvents, aircrafts, {
      outline: this.theme.hoverFlightColor,
      fill: ghostFlight,
    });
  };

  /**
   * getAircraftsInRange
   * @param numAircrafts - the number of total aircrafts
   * @returns number[] the min max index of the aircrafts in range
   */
  private getAircraftsInRange = (numAircrafts: number): number[] => {
    const [yMin, yMax] = this.camera.getYRange();
    const min = Math.floor(yMin / FlightGL.aircraftRowTotalHeight);
    const max = Math.ceil(yMax / FlightGL.aircraftRowTotalHeight);
    return [min, max >= numAircrafts ? numAircrafts - 1 : max];
  };

  /**
   * getSelectionDay
   * @param selectedDay - the day that is selected. Will be sometime in the day
   * @returns Date[] containing the start of the selected day, and the end of the selected day
   */
  private getSelectionDay = (selectedDay: Date): number[] => {
    if (!selectedDay) {
      return [];
    }
    const startTime = setBeginningDay(selectedDay.getTime()).getTime();
    const tempDate = new Date(startTime);
    tempDate.setDate(tempDate.getDate() + 1);
    const endTime = tempDate.getTime();
    return [startTime, endTime];
  };

  /**
   * A function to calculate the scroll bar dimensions
   */
  public recalculateScrollBar = (): void => {
    this.scrollBarWrapper.calculateScrollBar(this.camera, this.canvas, this.maxHeight, 15);
  };

  //snap to spot clicked
  public scrollClick(xPos: number, yPos: number): IScrollClickResponse["typeOfScroll"] {
    const response: IScrollClickResponse = this.scrollBarWrapper.clickedOnScrollBar(
      this.gl,
      this.camera,
      this.canvas,
      xPos,
      yPos,
      this.maxHeight
    );
    this.moveCameraByYPixels(response.pixelsToMove);
    if (response.typeOfScroll === 5) {
      this.scrollBarThumbColor = this.theme.scrollBarThumbClickedOn;
    } else {
      this.scrollBarThumbColor = this.theme.scrollBarThumb;
    }
    return response.typeOfScroll;
  }

  public hoveredOnScrollBarThumb(xPos: number, yPos: number): void {
    const response: boolean = this.scrollBarWrapper.hoveredOnScrollBarThumb(
      this.camera,
      this.canvas,
      xPos,
      yPos,
      this.maxHeight
    );
    if (response) {
      this.scrollBarThumbColor = this.theme.scrollBarThumbHover;
    } else {
      this.scrollBarThumbColor = this.theme.scrollBarThumb;
    }
  }

  //drag scroll bar
  public dragScrollBarThumb(yMovement: number): void {
    const [yStart, yEnd] = this.camera.getYRange(); //camera coordinates
    const pixelHeight: number = yEnd - yStart;
    const pixelsToMove: number = yMovement * (this.maxHeight / pixelHeight);
    this.moveCameraByYPixels(pixelsToMove);
  }

  /**
   * draw main drawing function for flight timeline
   * @param aircrafts - the list of aircrafts to iterate and draw each row
   * @param selections - the selections made by user which will be drawn/highlighted
   */
  public draw = (
    aircrafts: IAircraft[],
    selections: ITimelineSelections,
    selectedAircrafts: string[],
    highlightFlightSettings: IHighlightFlightSettings
  ): void => {
    if (!this.aircraftBuffers || this.gl.canvas.width === 0 || this.gl.canvas.height === 0) {
      return;
    }
    this.gl.clearColor(0.1, 0.1, 0.1, 1.0);

    // Clear the color buffer with specified clear color
    this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT);
    this.timelineLabelsGL.clear();
    this.timelineLabelsGL.prepareToDrawFlightLabels(this.theme.minorTimeColor);

    this.camera.updateProjectionMatrix();

    const selectionDay: number[] = this.getSelectionDay(selections.selectedDay);

    // draw flights fo  r each aircraft
    const [minIndex, maxIndex] = this.getAircraftsInRange(aircrafts.length);
    // if (aircrafts.length < 2) { return; }
    // const [minIndex, maxIndex] = [0, 2]
    for (let i = minIndex; i <= maxIndex; i++) {
      const aircraft = aircrafts[i];
      const aircraftBuffer = this.aircraftBuffers[aircraft._id ?? unassignedAircraftId];
      const y0 = FlightGL.aircraftRowTotalHeight * i;
      const aircraftIsSelected: boolean =
        (!!selections.selectedAircraft && selections.selectedAircraft === aircraft._id) ||
        selectedAircrafts?.includes(aircraft._id);
      const rowColor = aircraftIsSelected
        ? this.theme.selectedRow
        : i % 2 === 0
          ? this.theme.evenRow
          : this.theme.oddRow;

      this.aircraftRowPainter.drawScene(this.gl, this.camera, i, rowColor);

      if (aircraftBuffer) {
        const flightGL = aircraftBuffer.flights;
        const aircraftFlights: IFlight[] = flightGL.getFlights();
        const flightIndexesInRange = flightGL.updateFlightsInRange(this.camera);
        const [minIndexFlight, maxIndexFlight] = flightIndexesInRange;
        flightGL.draw(this.gl, this.camera, y0);

        const groundEventGL = aircraftBuffer.groundEvents;
        const groundEventsIndexesInRange = groundEventGL.updateGroundEventsInRange(this.camera);
        groundEventGL.draw(this.gl, this.camera, y0);
        //get the Flights &  GEs and sort them
        const [minIndexGE, maxIndexGE] = groundEventsIndexesInRange;
        const groundEvents = groundEventGL.getGroundEvents();
        const sortedEvents = sortAircraftEvents(
          aircraftFlights.slice(minIndexFlight, maxIndexFlight + 1),
          groundEvents.slice(minIndexGE, maxIndexGE + 1)
        );
        this.sortedAircraftEventsInRange[aircraft._id ?? unassignedAircraftId] = { events: sortedEvents };
        //now draw both the flight and ground event labels
        const flightErrors = (aircraftBuffer.eventErrors as any)?.errors;
        this.timelineLabelsGL.drawEventLabels(
          this.camera,
          sortedEvents,
          i,
          this.hoveredEvent,
          highlightFlightSettings,
          flightErrors
        );
        aircraftBuffer.eventErrors.draw(this.gl, this.camera, y0);

        const delayedFlights = flightGL.getDelayedFlights();
        const delayedFlightIndexesInRange = flightGL.getDelayedFlightsInRange(this.camera);
        this.timelineLabelsGL.drawDelayedFlightLabels(
          this.camera,
          delayedFlights,
          delayedFlightIndexesInRange,
          highlightFlightSettings
        );
      }

      if (selections.selectedDay && aircraftIsSelected) {
        this.selectedAircraftGL.draw(this.gl, this.camera, selectionDay, y0, hexToIRGBA(this.theme.intervalBar));
      }
    }

    this.drawHighlightedFlights(aircrafts);

    const currentTimeInPixels = this.camera.timeAxisToPixelCoords(Date.now(), this.canvas.width);

    if (currentTimeInPixels < this.canvas.width - ScrollBarWrapper.scrollBarWidth) {
      this.timelineLabelsGL.drawTimeIndicator(this.theme, currentTimeInPixels);
    }

    const hoveredTimeInPixels = this.camera.timeAxisToPixelCoords(this.hoveredTime, this.canvas.width);

    this.timelineLabelsGL.drawTimeAxis(this.camera, this.theme, {
      isUTC: true,
    });
    //to stop the bars being drawn on the scroll bar
    if (hoveredTimeInPixels < this.canvas.width - ScrollBarWrapper.scrollBarWidth) {
      this.timelineLabelsGL.drawHoveredTimeIndicator(this.theme, hoveredTimeInPixels);
    }

    this.timeAxisLines.updateBuffers(this.gl, this.getTimeRange(), { isUTC: true });
    this.timeAxisLines.draw(this.gl, this.camera, this.theme.timeAxisLineColor);

    //Draw the connection between flights on canvas
    this.drawConnection();

    if (selections.selectedDay) {
      this.selectedDayGL.draw(this.gl, this.camera, selectionDay, this.theme.blurEffect);
    }

    //draw last because disabling and enabling  depth buffer
    this.scrollBarWrapper.drawBar(
      this.gl,
      this.camera,
      this.canvas,
      this.theme.scrollBarBackGround,
      this.scrollBarThumbColor ?? this.theme.scrollBarThumb,
      this.maxHeight
    );
  };
}
